import { TFunction } from 'react-i18next';
import { getLocalizedDateWithoutLeadingZeroes } from '@arnold/core';
import styled from '@emotion/styled/macro';
import { BodyMedium, theme } from '@arnold/common';
import i18n from '../../translations/i18n';
import { CreditHistoryType, GetCreditHistoryQuery, OrganizationSettingQuery } from '../../generated/hooks';

const Type = styled.span`
  background-color: ${theme.colors.backgroundBasic.default};
  padding: ${theme.spacing.c} ${theme.spacing.d};
  border-radius: ${theme.spacing.c};
`;

const CreditsContainer = styled.div`
  text-align: right;
`;

type GetDataSourceParams = {
  /** Rows for the current table page */
  creditHistory: NonNullable<GetCreditHistoryQuery['getCreditHistory']>;
  /** Full history (same product period) for correct running totals when paginating */
  allCreditHistory: NonNullable<GetCreditHistoryQuery['getCreditHistory']>;
  /** Current credits product period (bought + transferred defines the pool for remaining balance) */
  productVersion?: NonNullable<NonNullable<OrganizationSettingQuery['organization']>['productVersionHistory']>[number];
  t: TFunction;
};

const getTotalCreditsPool = (
  productVersion?: NonNullable<NonNullable<OrganizationSettingQuery['organization']>['productVersionHistory']>[number],
): number | undefined => {
  if (!productVersion) return undefined;
  return Number(productVersion.boughtCredits ?? 0) + Number(productVersion.transferredCredits ?? 0);
};

const getCumulativeCreditsUsedByRecordId = (records: NonNullable<GetCreditHistoryQuery['getCreditHistory']>) => {
  const sorted = [...records].sort((a, b) => {
    const ta = new Date(a.createdAt || 0).getTime();
    const tb = new Date(b.createdAt || 0).getTime();
    if (ta !== tb) return ta - tb;
    return Number(a.id) - Number(b.id);
  });
  const map = new Map<string, number>();
  let cum = 0;
  for (const r of sorted) {
    cum += Number(r.creditsUsed) || 0;
    map.set(String(r.id), cum);
  }
  return map;
};

export type TransactionHistoryData = {
  id: string;
  date: string;
  organization: string;
  type: React.ReactNode;
  administrator: string;
  creditsUsed: React.ReactNode;
  creditsBalance: React.ReactNode;
};

const getType = (type: string, t: TFunction): JSX.Element => {
  let typeName;
  if (type === CreditHistoryType.OneTime) {
    typeName = t('oneTime');
  }
  if (type === CreditHistoryType.Onboarding) {
    typeName = t('onboarding');
  }
  if (type === CreditHistoryType.Exit) {
    typeName = t('exit');
  }
  if (type === CreditHistoryType.Hiring) {
    typeName = t('hiring');
  }
  if (type === CreditHistoryType.Process) {
    typeName = t('process');
  }
  if (type === CreditHistoryType.PublicSurvey) {
    typeName = t('publicSurvey');
  }
  if (type === CreditHistoryType.ManualDeduction) {
    typeName = t('manualDeduction');
  }
  if (type === CreditHistoryType.Pulser) {
    typeName = t('pulser');
  }
  return <Type>{typeName}</Type>;
};

const getUsedCredits = (creditsUsed: number) => {
  return (
    <CreditsContainer>
      <BodyMedium
        bold
        color={creditsUsed > 0 ? theme.colors.emotionSuccess.default : theme.colors.emotionDanger.default}
      >
        {creditsUsed}
      </BodyMedium>
    </CreditsContainer>
  );
};

const getCreditBalance = (creditsUsed: number) => {
  return (
    <CreditsContainer>
      <BodyMedium bold>{creditsUsed}</BodyMedium>
    </CreditsContainer>
  );
};

const getAdministratorName = (user: any) => {
  if (user.id === '1') {
    return 'Arnold';
  }
  return `${user.firstname || ''} ${user.surname || ''}`;
};

export const getDataSource = ({
  creditHistory,
  allCreditHistory,
  productVersion,
  t,
}: GetDataSourceParams): TransactionHistoryData[] => {
  const cumulativeById = getCumulativeCreditsUsedByRecordId(allCreditHistory);
  const totalPool = getTotalCreditsPool(productVersion);
  return creditHistory.map((record) => {
    const cumulativeRaw = cumulativeById.get(String(record.id)) ?? 0;
    const balanceRaw = totalPool !== undefined ? totalPool - cumulativeRaw : -cumulativeRaw;
    return {
      id: record.id,
      date: getLocalizedDateWithoutLeadingZeroes(new Date(record.createdAt || ''), i18n.language),
      organization: record.organization.name,
      type: getType(record.type, t),
      administrator: getAdministratorName(record.user),
      // Charges are stored positive; negate so spending shows as negative in the UI.
      creditsUsed: getUsedCredits(-(Number(record.creditsUsed) || 0)),
      creditsBalance: getCreditBalance(Math.round(balanceRaw * 100) / 100),
    };
  });
};
